import React, { useEffect, useState, FC } from 'react';
import { useRouter } from 'next/router';
import { FormattedMessage } from 'react-intl';
import { BreadCrumbs } from 'src/shared-components/design-system/BreadCrumbs';
import cn from 'classnames';
import useLocationStore from 'src/hooks/useLocationStore';
import postProductsByLetter, {
  PostProductsByLetterRequest
} from '../../api/requests/postProductsByLetter';
import { generateProductsByLetterPage } from '../../utils/links';
import Page from '../../shared-components/Page/Page';
import Constraints from '../../shared-components/Constraints/Constraints';
import trns from './trns';
import Pagination from '../../shared-components/Pagination/Pagination';
import { GetLettersResponse } from '../../api/requests/getLetters';
import NavigationLink from '../../shared-components/NavigationLink/NavigationLink';

const defaultPageSize = 120;

interface Props {
  drugListResponse: PostProductsByLetterRequest;
  lettersListResponse: GetLettersResponse[];
  pageNumber: number;
}

const DrugsByLetterContainer: FC<Props> = ({
  drugListResponse,
  lettersListResponse: letters,
  pageNumber
}) => {
  const router = useRouter();
  const { query, locale } = router;
  const { storeInitilized, userLocation } = useLocationStore();
  const { filters, sortBy, sortDirection, params } = query;
  const [letter] = params as string[];

  const [drugList, setDrugList] = useState<
    { translitName: string; name: string }[]
  >(drugListResponse.tradeNames || []);

  const [totalDrugsCount, setTotalDrugsCount] = useState<number>(
    drugListResponse?.totalCount || 1
  );
  const [selectedPages, setSelectedPages] = useState<number[]>([pageNumber]);

  useEffect(() => {
    setSelectedPages([pageNumber]);
  }, [pageNumber]);

  const handlePostProductsRequest = () => {
    const productsByLetterRequest = postProductsByLetter({
      pageSize: defaultPageSize,
      pageNumber,
      letter: letter as string
    });
    productsByLetterRequest
      .then((products) => {
        setDrugList(products.tradeNames);
        setTotalDrugsCount(products.totalCount);
      })
      .catch(() => {
        setDrugList([]);
        setTotalDrugsCount(1);
      });
  };

  useEffect(() => {
    if (storeInitilized) {
      handlePostProductsRequest();
    }
  }, [
    pageNumber,
    sortBy,
    sortDirection,
    // TODO: replace array with the primitive value
    filters,
    userLocation?.name,
    locale,
    letter
  ]);

  const lastPageIndex = Math.ceil(totalDrugsCount / defaultPageSize);

  const getPaginationLink = (newPageNumber: number) => {
    return generateProductsByLetterPage({
      letter: letter as string,
      page: newPageNumber
    });
  };

  const foundLetter = letters?.find((item) => item.translitName === letter);

  return (
    <Page letters={letters}>
      <Constraints className="pb-12 w-[90%]">
        <BreadCrumbs
          items={[
            {
              id: 'home',
              title: <FormattedMessage {...trns.main} />,
              href: '/'
            },
            {
              id: 'catalog',
              title: (
                <FormattedMessage
                  {...trns.letter}
                  values={{
                    letter: foundLetter.name
                  }}
                />
              )
            }
          ]}
        />
        <h1 className="fs-body-bold text-darkBlue text-[20px] lm:text-[28px] pt-[30px] pb-[25px] lm:pb-[50px] lm:pt-[30px] lm:pb-[50px] pl-4 lm:pl-0">
          <FormattedMessage
            {...trns.productsWithLetter}
            values={{
              letter: foundLetter.name
            }}
          />
        </h1>
        <div className="flex flex-col mb-[48px]">
          <div className="lm:border-b lm:border-spindle/50 inline-block flex lm:justify-between compendium-constraints w-full flex-wrap lm:leading-[18px] leading-[50px]">
            {letters?.map((letterItem, index) => {
              return (
                <NavigationLink
                  key={index}
                  href={{ pathname: `/letter/${letterItem.translitName}` }}
                >
                  <div
                    className={cn(
                      'cursor-pointer fs-body-lg-reg text-astronautBlue pb-[3px] px-[2px] lm:pr-0 pr-[10px]',
                      letter === letterItem.translitName &&
                        'lm:border-b-2 lm:border-astronautBlue text-primary'
                    )}
                  >
                    {letterItem.name}{' '}
                  </div>
                </NavigationLink>
              );
            })}
          </div>
          <div className="grid grid-cols-2 lm:grid-cols-4 mt-[25px] lm:mt-[60px]">
            {drugList.map((item, index) => (
              <NavigationLink
                key={index}
                href={{ pathname: `/tradename/${item.translitName}` }}
                passClassName="capitalize mb-[12px] cursor-pointer text-mako hover:text-astronautBlue"
              >
                {item.name.toLowerCase()}
              </NavigationLink>
            ))}
          </div>
          <div className="flex justify-center">
            <Pagination
              selectedPages={selectedPages}
              lastPageIndex={lastPageIndex || 1}
              getPaginationLink={getPaginationLink}
            />
          </div>
        </div>
      </Constraints>
    </Page>
  );
};

export default DrugsByLetterContainer;
